// §1 of brief 85 — THE SHARED FIELD VOCABULARY, declared once.
//
// Every entity modal in App.tsx was built from nine names — `_fl`, `_mt`,
// `F`, `S`, `T`, `TitSel`, `BarSel`, `BarAssoc`, `PF`, `NA` — declared
// INSIDE `renderModals`. Nothing outside that function could reach them, so a
// new modal written anywhere else had nothing to build on and had to re-invent
// the field row, the title selector, the bar selector and the name advisor.
// That is Laurent's first complaint ("always a guess about what to refer to
// GUI-wise") stated as a scope problem. After this file, the 18th modal is a
// lookup.
//
// MOVED, NOT REWRITTEN. Both bodies below are byte-identical to the lines they
// left (App.tsx:8691-8698 and :8699-8760 as of the parent commit), including
// their four-space indentation, which is why the bodies sit one level deeper
// than this file's own wrappers. Only the factory signatures and the returns
// are new. Do not reformat: the indentation IS the proof.
//
// THE ONE DUPLICATE THAT COLLAPSES: `_fl`/`_mt` were declared identically in
// `renderModals` and `renderQuickAddModal` (sha256-equal, both sides), so the
// quick-add copy is deleted and repointed at `makeLabelTranslators` here. Its
// OTHER names (QF/QS/QT/QTitSel/…) bind `quickAddForm`/`uqf` rather than
// `form`/`updateField` — a different form object, so they are not duplicates
// and they stay with that dialog until §4 moves it.
//
// A THIRD `_fl` [FINDING] — `renderPartyDetailDialog` declares one too
// (App.tsx:8249-8252), byte-identical to these. Brief 85 §1 says quick-add is
// "the one place where a duplicate collapses"; it is one of two. That dialog
// folds into this module when §4 moves it, which is the commit that owns it.
//
// TYPED, NOT `any`d — the ctx below carries real types so this file adds zero
// to the `any` ratchet. The three `any`s it does hold arrived inside the moved
// bodies and left App.tsx in the same commit; the global total is unchanged.

import FieldSelect from "./FieldSelect";
import type { FieldSelectOption } from "./FieldSelect";
import NameAdvisor from "./NameAdvisor";
import { makeFieldRows } from "../lib/fieldRows";
import { _LBL_KEYS, _MT_KEYS } from "../lib/modalLabelKeys";
import type { TaxList, TFn } from "../lib/taxonomyTypes";

export function makeLabelTranslators(t: TFn) {
    const _fl = (l: any) => {
      const k = _LBL_KEYS[l];
      return k ? t(k, { fallback: l }) : l;
    };
    const _mt = (s: any) => {
      const k = _MT_KEYS[s];
      return k ? t(k, { fallback: s }) : s;
    };
  return { _fl, _mt };
}

type ModalFieldsCtx = {
  t: TFn;
  form: Record<string, string | null | undefined>;
  updateField: (k: string, v: string) => void;
  titlesList?: readonly FieldSelectOption[] | null;
  titleEntries?: TaxList;
  barsList: readonly FieldSelectOption[];
  barEntries?: TaxList;
};

export function makeModalFields({
  t,
  form,
  updateField,
  titlesList,
  titleEntries,
  barsList,
  barEntries,
}: ModalFieldsCtx) {
  const { _fl, _mt } = makeLabelTranslators(t);
    // A3 (brief 65) — the three row builders live in src/lib/fieldRows, taken
    // under their old names so every call site below is unchanged.
    const { F, S, T } = makeFieldRows({
      form,
      update: updateField,
      fl: _fl,
      t,
    });
    const TitSel = (
      <FieldSelect
        label={_fl("Title")}
        value={form.title}
        onChange={(v) => updateField("title", v)}
        options={[["", "—"], ...(titlesList || [])]}
        entries={titleEntries}
        t={t}
      />
    );
    // 05r — the modal half of the same repair. See QBarSel above for why.
    const BarSel = (
      <FieldSelect
        label={_fl("Bar")}
        value={form.bar}
        onChange={(v) => updateField("bar", v)}
        options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...barsList]}
        entries={barEntries}
        t={t}
      />
    );
    const BarAssoc = (
      <FieldSelect
        label={_fl("Professional Registration")}
        value={form.barAssociation}
        onChange={(v) => updateField("barAssociation", v)}
        options={[["", `— ${t("common.none", { fallback: "None" })} —`], ...barsList]}
        entries={barEntries}
        t={t}
      />
    );
    const PF = (
      <>
        <div className="field-row">
          {TitSel}
          {F("First Name *", "firstName", "", 1)}
        </div>
        {F("Last Name *", "lastName")}
      </>
    );
    // Phase 43 CORRECTION (item 6) — shared live name-advisory. Rendered under a
    // person modal's name fields; reads the whole form `f` (so it works for both
    // company and individual name shapes). cfg.pool = same-type pool (duplicate);
    // cfg.coiPool = archived-inclusive client pool (conflict, opponents only).
    const NA = (cfg: any = {}) => (
      <NameAdvisor
        form={form}
        pool={cfg.pool || []}
        coiPool={cfg.coiPool}
        typeLabel={cfg.typeLabel}
        excludeId={form.id}
        t={t}
      />
    );
  return { _fl, _mt, F, S, T, TitSel, BarSel, BarAssoc, PF, NA };
}
